import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMonthDay, formatPreset, formatWeekday } from '@/i18n/intl';

export interface WeekdayDateOptions {
  /** The value is date-only (stored as UTC midnight): read its UTC calendar day (B-432). */
  dateOnly?: boolean;
  /** "Thu Sep 12, 2026" instead of "Thu Sep 12". */
  year?: boolean;
  /** Rendered for an empty or unparseable value. */
  fallback?: string;
}

/**
 * "Thu Sep 12" — the Health surfaces’ weekday-plus-date stamp, through Intl
 * (#2109 R3-B5). The weekday and the date are locale data; their order is copy
 * (`health:date.weekdayDate`), so English stays the date-fns `EEE MMM d` it
 * replaced. A bare `YYYY-MM-DD` that is not date-only is read as local
 * midnight, as `parseISO` did.
 */
export function useWeekdayDate(): (value: string | null | undefined, options?: WeekdayDateOptions) => string {
  const { t } = useTranslation(['health', 'common']);
  return useCallback(
    (value, options = {}) => {
      const fallback = options.fallback ?? '—';
      if (!value) return fallback;
      const bareDay = /^\d{4}-\d{2}-\d{2}$/.test(value);
      const d = new Date(bareDay && !options.dateOnly ? `${value}T00:00:00` : value);
      if (Number.isNaN(d.getTime())) return fallback;
      const zone = { dateOnly: options.dateOnly };
      return t('health:date.weekdayDate', {
        weekday: formatWeekday(d, zone),
        date: options.year ? formatPreset(d, 'monthDayYear', zone) : formatMonthDay(d, zone),
      });
    },
    [t],
  );
}
